import { Card, CardContent } from '@daily/shared/components/Card';
import { LoadingSpinner } from '@daily/shared/components/LoadingSpinner';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useDaily, useLocalSessionId } from '@daily-co/daily-react';
import { useTranslation } from 'react-i18next';

import { useBreakoutSessionState } from './hooks/useBreakoutSessionState';
const MAX_ALSO_JOINING_COUNT = 10;

export const JoiningBreakoutRoomScreen: React.FC = () => {
  const daily = useDaily();
  const { t } = useTranslation();
  const { invitedBreakoutRoom } = useBreakoutSessionState();
  const participants = daily?.participants();
  const localSessionId = useLocalSessionId();

  const joiningPeers = (invitedBreakoutRoom?.users ?? []).filter(
    (id) => id !== localSessionId
  );

  const joiningPeersWithName = joiningPeers.filter((id) =>
    Boolean(participants?.[id]?.user_name)
  );

  const displayableNames = joiningPeersWithName.slice(
    0,
    MAX_ALSO_JOINING_COUNT
  );

  const nonDisplayableNamesCount = Math.max(
    0,
    joiningPeersWithName.length - MAX_ALSO_JOINING_COUNT
  );

  const joiningPeersWithoutNameCount =
    joiningPeers.length - joiningPeersWithName.length;

  const names = [
    ...(displayableNames.length
      ? displayableNames.map((id) => participants[id].user_name)
      : []),
  ];

  if (joiningPeers.length > 0 && joiningPeersWithoutNameCount > 0) {
    const namesCount = joiningPeersWithoutNameCount + nonDisplayableNamesCount;

    names.push(
      t('people.othersWithCount', {
        count: namesCount,
      })
    );
  }

  return (
    <Card
      noBorder
      shadow="none"
      appearance="grey"
      style={{ justifyContent: 'center' }}
    >
      <CardContent>
        <div className="joining-breakout-room-screen">
          <Stack align="center" gap={8}>
            <LoadingSpinner />
            <Text El="h2" variant="largestrong">
              {t('breakouts.joiningBreakoutScreen.description', {
                room: invitedBreakoutRoom
                  ? t('breakouts.roomWithName', {
                      name: invitedBreakoutRoom?.name,
                    })
                  : t('breakouts.mainRoom'),
              })}
            </Text>
            {invitedBreakoutRoom && (
              <Text El="p" className="joining-user-names">
                {joiningPeers.length > 0
                  ? t('breakouts.joiningBreakoutScreen.joiningUsers', {
                      count: names.length,
                      names,
                    })
                  : t('breakouts.joiningBreakoutScreen.standBy')}
              </Text>
            )}
            {!invitedBreakoutRoom && (
              <Text El="p">{t('breakouts.joiningBreakoutScreen.standBy')}</Text>
            )}
          </Stack>
        </div>
      </CardContent>

      <style jsx>{`
        .joining-breakout-room-screen {
          text-align: center;
          margin-bottom: 16px;
        }
        .joining-breakout-room-screen :global(.joining-user-names) {
          max-width: 80ch;
        }
        // the loading spinner. we want the text to be centered vertically regardless of the spinner.
        .joining-breakout-room-screen :global(svg) {
          margin-bottom: 16px;
        }
      `}</style>
    </Card>
  );
};
